import type { FC } from "react";
import { useState, useCallback } from "react";
import { Button } from "@/components/flexkit/Button";
import { Text } from "@/components/podkit/typography/Text";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useDeleteHostAuthenticationToken } from "@/queries/runner-configuration-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import type { ServiceAccountHeaders } from "@/api";
import type { IntegrationWithAuth } from "@/components/settings/organization/service-accounts/ServiceAccountIntegrationsList";
import { useDesktop } from "@/hooks/use-desktop";
import { appMainService } from "@/ipc";

export type ServiceAccountIntegrationCardProps = {
    integrationWithAuth: IntegrationWithAuth;
    headers: ServiceAccountHeaders;
    serviceAccountId: string;
};

export const ServiceAccountIntegrationCard: FC<ServiceAccountIntegrationCardProps> = ({
    integrationWithAuth,
    headers,
    serviceAccountId,
}) => {
    const { toast } = useToast();
    const { isDesktop } = useDesktop();
    const { data: user } = useAuthenticatedUser();
    const deleteTokenMutation = useDeleteHostAuthenticationToken({ headers });

    const [isConnecting, setIsConnecting] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState(false);

    const { integration, definition, isConnected } = integrationWithAuth;
    const isDisabled = !integration.enabled;

    const handleConnect = useCallback(
        (userId: string) => {
            if (isDisabled) {
                toast({
                    title: "Integration disabled",
                    description: "This integration is currently disabled and cannot be connected to.",
                });
                return;
            }

            setIsConnecting(true);

            const isGitHubApp = definition?.auth?.proprietaryApp !== undefined;

            if (isGitHubApp) {
                toast({
                    title: "Not supported",
                    description: "GitHub App integrations are not yet supported for service accounts.",
                });
                setIsConnecting(false);
                return;
            }

            const params = new URLSearchParams({
                principal: `user/${userId}`,
                service_account: serviceAccountId,
            });
            const authUrl = `${window.location.origin}/integrations/oauth/${integration.id}/authorize?${params.toString()}`;

            if (isDesktop && appMainService) {
                void appMainService.openExternal({ url: authUrl });
            } else {
                window.open(authUrl, "_blank", "noopener");
            }

            // Reset connecting state after a short delay since we can't monitor the popup
            setTimeout(() => {
                setIsConnecting(false);
            }, 1000);

            toast({
                title: "OAuth window opened",
                description:
                    "Complete the authorization in the popup window. Your connection status will update automatically.",
            });
        },
        [integration.id, definition?.auth?.proprietaryApp, serviceAccountId, isDisabled, toast, isDesktop],
    );

    const handleDisconnect = useCallback(async () => {
        if (!integrationWithAuth.authToken) return;

        setIsDisconnecting(true);

        try {
            await deleteTokenMutation.mutateAsync({
                tokenId: integrationWithAuth.authToken.id,
            });

            toast({
                title: "Integration disconnected",
                description: "The service account has been disconnected from this integration.",
            });
        } catch (error) {
            console.error("Failed to disconnect integration:", error);
            toast({
                title: "Disconnection failed",
                description: "Failed to disconnect the integration. Please try again.",
                variant: "default",
            });
        } finally {
            setIsDisconnecting(false);
        }
    }, [integrationWithAuth.authToken, deleteTokenMutation, toast]);

    return (
        <div className="flex items-center justify-between gap-2 rounded-xl border-0.5 border-border-base bg-surface-primary px-4 py-3 @container">
            <div className="flex items-center gap-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg">
                    {definition?.iconUrl ? (
                        <img src={definition.iconUrl} alt={`${definition.name} icon`} className="size-8" />
                    ) : (
                        <div className="size-6 rounded bg-content-secondary" />
                    )}
                </div>
                <div className="flex flex-col">
                    <Text className={`text-base font-bold ${isDisabled ? "text-content-muted" : ""}`}>
                        {definition?.name || integration.integrationDefinitionId}
                    </Text>
                    <Text className="hidden text-sm text-content-secondary @sm:block">
                        {definition?.description || "Organization integration"}
                    </Text>
                </div>
            </div>

            <div className="flex items-center gap-2">
                {isConnected ? (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleDisconnect}
                        disabled={isDisconnecting}
                        data-testid={`disconnect-integration-${integration.id}`}
                        data-tracking-id="disconnect-integration"
                    >
                        {isDisconnecting ? "Disconnecting..." : "Disconnect"}
                    </Button>
                ) : (
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => user?.id && handleConnect(user.id)}
                        disabled={isConnecting || isDisabled || !user?.id}
                        data-testid={`connect-integration-${integration.id}`}
                        data-tracking-id="connect-integration"
                    >
                        {isConnecting ? "Connecting..." : "Connect"}
                    </Button>
                )}
            </div>
        </div>
    );
};
